<template>
  <div
    class="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
    @click.self="close"
  >
    <div class="bg-white dark:bg-gray-800 w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl p-6 max-h-[90vh] overflow-y-auto">
      <!-- Success state -->
      <div v-if="done" class="text-center py-6">
        <div class="text-4xl mb-3">🙏</div>
        <h3 class="text-lg font-bold text-gray-900 dark:text-gray-100">Thank you!</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Your feedback goes straight to the team — it really helps shape the beta.</p>
        <button
          @click="close"
          class="mt-5 bg-emerald-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-emerald-700"
        >
          Close
        </button>
      </div>

      <!-- Form -->
      <div v-else>
        <div class="flex items-start justify-between">
          <h3 class="text-lg font-bold text-gray-900 dark:text-gray-100">Share feedback</h3>
          <button @click="close" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none">&times;</button>
        </div>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
          We just reworked the lessons and course order. Tell us how it feels — what's better, what's confusing, anything at all.
        </p>

        <fieldset class="mt-4 grid grid-cols-2 gap-2">
          <label
            v-for="t in topics"
            :key="t"
            class="flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer transition-colors text-sm font-medium"
            :class="topic === t
              ? 'border-emerald-400 bg-emerald-50 text-gray-900 dark:bg-emerald-900/30 dark:text-emerald-100 dark:border-emerald-500'
              : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300'"
          >
            <input type="radio" name="feedback-topic" :value="t" v-model="topic" class="accent-emerald-600" />
            <span>{{ t }}</span>
          </label>
        </fieldset>

        <textarea
          ref="commentEl"
          v-model="comment"
          rows="4"
          maxlength="2000"
          placeholder="What's on your mind?"
          class="mt-3 w-full rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 p-3 text-sm focus:border-emerald-400 focus:ring-0"
        ></textarea>

        <p v-if="error" class="text-sm text-red-500 mt-2">{{ error }}</p>

        <div class="mt-4 flex gap-3 justify-end">
          <button
            @click="close"
            class="px-5 py-2.5 rounded-xl font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            @click="submit"
            :disabled="!comment.trim() || submitting"
            class="px-5 py-2.5 rounded-xl font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ submitting ? 'Sending…' : 'Send feedback' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import { apiPost } from '../../api/client'
import { track } from '../../utils/analytics'

const emit = defineEmits(['close', 'submitted'])

const topics = [
  'Course structure & ordering',
  'A lesson or exercise',
  'Something’s broken',
  'General thoughts',
]

const topic = ref('General thoughts')
const comment = ref('')
const submitting = ref(false)
const done = ref(false)
const error = ref('')
const commentEl = ref(null)

onMounted(() => { nextTick(() => commentEl.value?.focus()) })

async function submit() {
  if (!comment.value.trim() || submitting.value) return
  submitting.value = true
  error.value = ''
  try {
    // Reuses the exercise-report pipeline (email → Riley) with the app_feedback
    // category; no exercise snapshot, just the topic + comment.
    await apiPost('/api/v1/exercise_reports', {
      category: 'app_feedback',
      comment: comment.value.trim(),
      context: {
        feedback_topic: topic.value,
        url: window.location.href,
      },
    })
    done.value = true
    track('app_feedback_submitted', { topic: topic.value })
    emit('submitted')
  } catch (e) {
    error.value = "Sorry, that didn't send. Please try again."
  } finally {
    submitting.value = false
  }
}

function close() {
  emit('close')
}
</script>
